import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LoadingOverlay, Box } from "@mantine/core";
import { useNavigate } from "react-router-dom";

// Context
import { useSalon } from "../../../context/SalonContext";
import { useCalendar } from "../../../context/CalendarContext";
import { useResponsive } from "../../../context/ResponsiveContext";
import { usePermissions } from "../../../hooks/usePermissions";

// Utils
import { getEffectiveScope } from "../../../utils/permissionFilters";
import { getSalonTimezone } from "../../../utils/timeUtils";
import { transformTransactionResponseToGetTransactionsResponse } from "../../../utils/transactionUtils";
import type {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../types/transactions";

// Components
import Header from "./components/Header";
import { DayView, WeekView, ListView, CardView } from "./components/Views";
import FloatingActionButton from "./components/FloatingActionButton";
import TransactionDetailModal from "./components/TransactionDetailModal/TransactionDetailModal";
import QuickSaleModal from "./components/QuickSaleModal";
import TrialBanner from "./components/TrialBanner";
import PreviewSurfaces from "./components/PreviewSurfaces";
import SchedulingModals from "./components/SchedulingModals";
import DragConfirmModal from "./components/DragConfirmModal";
import SlotChooser from "./components/SlotChooser";
import BookingPanel from "./booking/BookingPanel";
import { calendarDrawerWidthVar } from "./drawer/CalendarDrawer";
import {
  MobileBookingWiring,
  MobileCalendarSurface,
  MobileDetailWiring,
} from "./mobile";

// Hooks (one per concern)
import { useCalendarData } from "./hooks/useCalendarData";
import { useTransactionStore } from "./hooks/useTransactionStore";
import { useSchedulingActions } from "./hooks/useSchedulingActions";
import { usePreviewPopover } from "./hooks/usePreviewPopover";
import { useRealtimeSync } from "./hooks/useRealtimeSync";
import { useDeepLinkTarget } from "./hooks/useDeepLinkTarget";
import { useDragReschedule } from "./hooks/useDragReschedule";
import { useRevealBesideDrawer } from "./hooks/useRevealBesideDrawer";
import { useFilteredEvents } from "./hooks/useFilteredEvents";
import { useBookingSession } from "./booking/useBookingSession";
import { useBookingViewProps } from "./booking/useBookingViewProps";
import { useDrawerSlot } from "./drawer/drawerSlot";
import { useWaitlist } from "./waitlist/useWaitlist";
import { useWaitlistDrawer } from "./waitlist/useWaitlistDrawer";
import WaitlistDrawer from "./waitlist/WaitlistDrawer";
import { dateOfDay, dayOf, isDayInView } from "./booking/bookingTypes";
import { calendarFlags } from "./flags/calendarFlags";
import { badgeSettingsFrom } from "../../../utils/badgeSettings";
import { getSalonNow } from "./components/Views/sharedCalendarUtils";
import type {
  CalendarEventType,
  TimeBlockEvent,
} from "../../../types/calendar";
import {
  CalendarBusinessInfo,
  useTypedCalendarEvent,
} from "./calendarViewTypes";

import styles from "./CalendarView.module.css";

const CalendarView: React.FC = () => {
  const navigate = useNavigate();

  // Context hooks
  const {
    businessInfo,
    isMissingSalon,
    services: salonServices,
    currentUser,
    availableLocations,
    refreshServices,
  } = useSalon() as {
    businessInfo: CalendarBusinessInfo | null;
    isMissingSalon: boolean | null;
    services: any[];
    currentUser: { id: string } | null;
    availableLocations: any[] | undefined;
    refreshServices: () => Promise<unknown>;
  };
  // Multi-location flag ON ⇔ /access advertises available_locations. Gates the
  // extra POST /timeoff field so the calendar stays inert (byte-identical
  // requests) for single-location users until the backend accepts the field.
  const multiLocationOn = (availableLocations?.length ?? 0) > 0;
  const timezone = businessInfo?.timezone;
  const salonTimezone = getSalonTimezone(businessInfo);

  // Get calendar scope permission for filtering
  const permissions = usePermissions();
  const calendarScope = getEffectiveScope(permissions?.calendar?.scope);
  const currentUserId = currentUser?.id || "";

  const {
    viewType,
    selectedDate,
    setSelectedDate,
    dateRange,
    setDateRange,
    filteredEmployees,
  } = useCalendar();
  const { events } = useTypedCalendarEvent();
  const { isMobile } = useResponsive();

  // Redirect to business info if missing salon
  useEffect(() => {
    if (isMissingSalon === true) {
      navigate("/business-info");
    }
  }, [isMissingSalon, navigate]);

  const data = useCalendarData({
    businessInfo,
    isMissingSalon,
    salonServices,
    selectedDate,
    setSelectedDate,
    dateRange,
  });

  const store = useTransactionStore({ timezone, salonServices });

  // One drawer at a time: appointment, booking, time block and walk-ins
  // drawers each take this slot, evicting the others.
  const drawerSlot = useDrawerSlot();

  // Walk-in waitlist: the store lives here so the header count stays live
  // while the drawer is closed. Ungated, like the FAB create items (the
  // backend allows any active staff at the location).
  const waitlist = useWaitlist({
    enabled: !!businessInfo?.id,
  });
  const walkInsDrawer = useWaitlistDrawer(drawerSlot);

  const scheduling = useSchedulingActions({
    businessId: businessInfo?.id,
    timezone,
    salonServices,
    multiLocationOn,
    selectedDate,
    refreshServices,
    mapTimeBlocks: data.mapTimeBlocks,
    refetchMonthBuffer: data.refetchMonthBuffer,
    drawerSlot,
  });

  const preview = usePreviewPopover({
    salonUuid: businessInfo?.id,
    isMobile,
    findTransaction: store.findTransaction,
    openTransactionModal: store.openTransactionModal,
    openTimeBlockEditor: scheduling.openTimeBlockEditor,
    handleTransactionUpdated: store.handleTransactionUpdated,
    applyAppointmentStatus: store.applyAppointmentStatus,
    drawerSlot,
  });

  useRealtimeSync({
    transactions: store.transactions,
    events,
    refetchAndUpdateTransaction: store.refetchAndUpdateTransaction,
    fetchAppointmentsAndTimeBlocks: data.fetchAppointmentsAndTimeBlocks,
  });

  const { targetTab, clearTargetTab } = useDeepLinkTarget({
    timezone,
    salonServices,
    setSelectedDate,
    setDateRange,
    handleEventSelect: preview.handleEventSelect,
    performPredictiveFetch: data.performPredictiveFetch,
    refetchMonthBuffer: data.refetchMonthBuffer,
    setIsInitialLoading: data.setIsInitialLoading,
    setIsLoading: scheduling.setIsLoading,
  });

  const drag = useDragReschedule({
    timezone,
    propagateBookedServiceEdit: store.propagateBookedServiceEdit,
    salonServices,
    staff: filteredEmployees,
  });

  const { filteredEvents, scopedEvents } = useFilteredEvents({
    events,
    transactions: store.transactions,
    calendarScope,
    currentUserId,
  });

  const { closeTransactionModal } = store;
  const handleTransactionModalClose = useCallback(() => {
    closeTransactionModal();
    clearTargetTab();
  }, [closeTransactionModal, clearTargetTab]);

  const booking = useBookingSession({
    timezone: salonTimezone,
    salonServices,
    isMobile,
    stashAndClosePreview: preview.stashAndClosePreview,
    reopenPreview: preview.reopenPreview,
    dropPreviewStash: preview.dropPreviewStash,
    openEventPreview: preview.openEventPreview,
    storeTransaction: store.storeTransaction,
    refetchAndUpdateTransaction: store.refetchAndUpdateTransaction,
    drawerSlot,
  });

  // Flag chips for a grid block: calendarFlags over the store's transaction,
  // with the salon's badge toggles.
  const { findTransaction } = store;
  const badgeSettings = useMemo(
    () => badgeSettingsFrom(businessInfo),
    [businessInfo],
  );
  const flagsForEvent = useCallback(
    (event: CalendarEventType) => {
      if (event.isTimeBlock || !("transactionId" in event)) return [];
      return calendarFlags({
        txn: findTransaction(event.transactionId),
        status: event.status,
        start: event.start,
        end: event.end,
        now: getSalonNow(salonTimezone),
        timezone: salonTimezone,
        settings: badgeSettings,
      });
    },
    [findTransaction, salonTimezone, badgeSettings],
  );

  // Day / Week props: normal handlers, or the booking session's while its
  // panel is open (temp events, row selection, event-click routing,
  // slot/drop moves).
  const viewProps = useBookingViewProps(booking, {
    events: filteredEvents,
    onSelectEvent: preview.handleEventSelect,
    onDoubleClickEvent: preview.handleEventDoubleClick,
    onSelectSlot: scheduling.handleSlotSelect,
    onEventDrop: drag.handleEventDrop,
  });

  // A drawer (CalendarDrawer: booking or appointment) is open: the grid
  // reflows beside it and the FAB / header tabs shift left of it.
  const drawerOpen =
    booking.panelOpen ||
    !!preview.previewData ||
    scheduling.isTimeBlockModalOpen ||
    walkInsDrawer.opened;

  // Day / Week event click: once the drawer it opens has pushed the grid,
  // scroll the clicked event back into view beside it.
  const viewAreaRef = useRef<HTMLDivElement | null>(null);
  const revealBesideDrawer = useRevealBesideDrawer(drawerOpen, viewAreaRef);
  const { onSelectEvent: viewSelectEvent } = viewProps;
  const handleGridSelectEvent = useCallback(
    (event: CalendarEventType, e?: React.SyntheticEvent) => {
      revealBesideDrawer(e?.currentTarget);
      viewSelectEvent(event, e);
    },
    [revealBesideDrawer, viewSelectEvent],
  );

  // A new time block's dashed preview on the grid while its drawer is open.
  const [timeBlockDraft, setTimeBlockDraft] = useState<TimeBlockEvent | null>(
    null,
  );
  const timeBlockOpen = scheduling.isTimeBlockModalOpen;
  const gridEvents = useMemo(
    () =>
      timeBlockDraft && timeBlockOpen
        ? [...viewProps.events, timeBlockDraft]
        : viewProps.events,
    [viewProps.events, timeBlockDraft, timeBlockOpen],
  );

  // New appointment: the booking drawer on desktop, the legacy modal on
  // mobile.
  const { startNewAppointment, startFromSlot } = booking;
  const {
    handleNewAppointment: openLegacyAppointment,
    openAppointmentModalForSlot,
    closeSlotChooserForBooking,
    selectedSlot,
  } = scheduling;
  const handleNewAppointment = isMobile
    ? openLegacyAppointment
    : startNewAppointment;
  const handleChooseAppointment = useCallback(() => {
    if (isMobile || !selectedSlot) {
      openAppointmentModalForSlot();
      return;
    }
    closeSlotChooserForBooking();
    startFromSlot(selectedSlot);
  }, [
    isMobile,
    selectedSlot,
    openAppointmentModalForSlot,
    closeSlotChooserForBooking,
    startFromSlot,
  ]);

  // Mobile appointment detail sheet: the same store / popover paths desktop's
  // event preview popover uses (status write, Appointment Details, forms,
  // client notes), plus the store refetch after a reschedule.
  const { refetchAndUpdateTransaction, handleTransactionUpdated } = store;

  // A walk-in was assigned: the existing single-transaction refetch puts the
  // new appointment on the grid, and the view moves to today when today is
  // not on screen (prototype).
  const handleWalkInAssigned = useCallback(
    (transactionUuid: string) => {
      void refetchAndUpdateTransaction(transactionUuid, "walk-in assign");
      const today = dayOf(getSalonNow(salonTimezone));
      if (!isDayInView(today, viewType, selectedDate))
        setSelectedDate(dateOfDay(today));
    },
    [
      refetchAndUpdateTransaction,
      salonTimezone,
      viewType,
      selectedDate,
      setSelectedDate,
    ],
  );
  const mobileDetail: MobileDetailWiring = useMemo(
    () => ({
      findTransaction: store.findTransaction,
      onStatusChange: store.applyAppointmentStatus,
      onView: preview.handleOpenTransactionModal,
      onOpenForm: preview.handleOpenForm,
      onAddForm: preview.handleAddForm,
      sentFormTxnIds: preview.sentFormTxnIds,
      onFormsSent: preview.markFormsSent,
      onFormsChange: (txn, forms) =>
        void handleTransactionUpdated({ ...txn, forms }),
      onTagsChange: (txn, tags) =>
        void handleTransactionUpdated({ ...txn, tags }),
      refetchTransactions: (ids) =>
        ids.forEach(
          (id) => void refetchAndUpdateTransaction(id, "mobile reschedule"),
        ),
    }),
    [
      store.findTransaction,
      store.applyAppointmentStatus,
      refetchAndUpdateTransaction,
      handleTransactionUpdated,
      preview.handleOpenTransactionModal,
      preview.handleOpenForm,
      preview.handleAddForm,
      preview.sentFormTxnIds,
      preview.markFormsSent,
    ],
  );

  // Mobile new appointment sheet (the shared booking session) + rebook
  // screen; created transactions go into the same store as desktop's.
  const { storeTransaction } = store;
  const storeCreated = useCallback(
    (created: TransactionResponse[]) =>
      created.forEach((response) =>
        storeTransaction(
          transformTransactionResponseToGetTransactionsResponse(response),
        ),
      ),
    [storeTransaction],
  );
  // The mobile rebook screen's appointment (detail sheet or Appointment
  // Details → Rebook).
  const [mobileRebookTxn, setMobileRebookTxn] =
    useState<GetTransactionsResponse | null>(null);
  const mobileBooking: MobileBookingWiring = {
    session: booking,
    storeCreated,
    rebookTxn: mobileRebookTxn,
    setRebookTxn: setMobileRebookTxn,
  };

  // Appointment Details → Rebook: the new flow (desktop: the booking drawer
  // in rebook mode; mobile: the rebook screen), after closing the details.
  const { startRebook } = booking;
  const handleDetailsRebook = useCallback(
    (txn: GetTransactionsResponse | TransactionResponse) => {
      const full: GetTransactionsResponse =
        "reminder_messages" in txn
          ? txn
          : transformTransactionResponseToGetTransactionsResponse(txn);
      handleTransactionModalClose();
      if (isMobile) setMobileRebookTxn(full);
      else startRebook(full);
    },
    [handleTransactionModalClose, isMobile, startRebook],
  );

  const loadingOverlay = (
    // Full-page loader while fetching a just-rebooked appointment from the
    // event-popover flow (no transaction modal is open yet to show its own
    // loader; the transaction-modal flow already handles this internally).
    <LoadingOverlay
      visible={
        data.isInitialLoading ||
        (store.isLoadingNewTransaction && !store.isTransactionModalOpen)
      }
    />
  );

  const sharedModals = (
    <>
      <SchedulingModals
        scheduling={scheduling}
        isMobile={isMobile}
        drawerSlot={drawerSlot}
        events={events}
        timezone={salonTimezone}
        viewedDay={selectedDate}
        onTimeBlockDraftChange={setTimeBlockDraft}
      />

      {/* Transaction Detail Modal */}
      <TransactionDetailModal
        opened={store.isTransactionModalOpen}
        onClose={handleTransactionModalClose}
        transaction={store.selectedTransaction}
        onTransactionUpdated={store.handleTransactionUpdated}
        onViewNewAppointment={store.handleViewNewAppointment}
        onRebook={handleDetailsRebook}
        isLoadingNewTransaction={store.isLoadingNewTransaction}
        initialTab={targetTab}
      />

      {/* Quick Sale Modal */}
      <QuickSaleModal
        opened={scheduling.isQuickSaleModalOpen}
        onClose={scheduling.closeQuickSaleModal}
        onQuickSaleComplete={scheduling.handleQuickSaleComplete}
      />
    </>
  );

  // Phone (≤768px): the mobile calendar surface replaces the header, views,
  // FAB and booking drawer. Appointment taps open the mobile detail sheet
  // (View → Appointment Details); time-block taps open the time-block edit
  // modal. New Appointment and the slot
  // menu's Appointment open the mobile new-appointment sheet over the same
  // booking session as desktop's drawer; Walk-in opens the New walk-in
  // screen over the same waitlist store as desktop's drawer.
  if (isMobile) {
    return (
      <>
        {loadingOverlay}
        <MobileCalendarSurface
          events={filteredEvents}
          scopedEvents={scopedEvents}
          onTimeBlockTap={scheduling.openTimeBlockEditor}
          detail={mobileDetail}
          booking={mobileBooking}
          onNewAppointment={booking.startBlankAppointment}
          onNewTimeBlock={scheduling.handleNewTimeBlock}
          walkIns={{ store: waitlist, onAssigned: handleWalkInAssigned }}
          onSlotAppointment={booking.startFromSlot}
          onSlotTimeBlock={scheduling.openTimeBlockAt}
          banner={<TrialBanner businessInfo={businessInfo} />}
          transactions={store.transactions}
          flagsForEvent={flagsForEvent}
        />
        <PreviewSurfaces
          preview={preview}
          salonUuid={businessInfo?.id}
          timezone={salonTimezone}
          onRebook={booking.startRebook}
          onReschedule={booking.startReschedule}
          drawerSlot={drawerSlot}
        />
        {sharedModals}
      </>
    );
  }

  // Render the appropriate view
  const renderView = () => {
    switch (viewType) {
      case "week":
        return (
          <WeekView
            events={gridEvents}
            onSelectEvent={handleGridSelectEvent}
            onDoubleClickEvent={viewProps.onDoubleClickEvent}
            onSelectSlot={viewProps.onSelectSlot}
            onEventDrop={viewProps.onEventDrop}
            onEventResize={drag.handleEventResize}
            draggableAccessor={viewProps.draggableAccessor}
            resizableAccessor={viewProps.resizableAccessor}
            flagsForEvent={flagsForEvent}
          />
        );
      case "list":
        return (
          <ListView
            transactions={store.transactions}
            onSelectTransaction={preview.handleOpenTransactionModal}
          />
        );
      case "card":
        return (
          <CardView
            transactions={store.transactions}
            onSelectTransaction={preview.handleOpenTransactionModal}
            onStatusChange={store.handleCardStatusUpdate}
          />
        );
      case "day":
      default:
        return (
          <DayView
            events={gridEvents}
            onSelectEvent={handleGridSelectEvent}
            onDoubleClickEvent={viewProps.onDoubleClickEvent}
            onSelectSlot={viewProps.onSelectSlot}
            onEventDrop={viewProps.onEventDrop}
            onEventResize={drag.handleEventResize}
            draggableAccessor={viewProps.draggableAccessor}
            resizableAccessor={viewProps.resizableAccessor}
            scrollTimeOfDay={viewProps.scrollTimeOfDay}
            flagsForEvent={flagsForEvent}
            blockedStaff={booking.blockedStaff ?? drag.dragBlockedStaff}
            onDragStart={booking.panelOpen ? undefined : drag.handleDragStart}
          />
        );
    }
  };

  // FAB actions interrupt an open booking draft through its discard gate.
  const { requestCloseThen } = booking;
  // Header Walk-Ins: toggles the walk-ins drawer (a booking draft goes
  // through its discard gate first).
  const toggleWalkIns = () =>
    walkInsDrawer.opened
      ? walkInsDrawer.close()
      : requestCloseThen(walkInsDrawer.open);

  return (
    <>
      {loadingOverlay}

      <div className={styles.calendarView} style={calendarDrawerWidthVar}>
        {/* Trial Banner — kept inside .calendarView so the 100dvh height budget
            includes the banner instead of overflowing the viewport. */}
        <TrialBanner businessInfo={businessInfo} />

        {/* Header */}
        <Header
          schedulingOpen={booking.panelOpen}
          drawerOpen={drawerOpen}
          focusMode={booking.focusMode}
          walkIns={{
            count: waitlist.count,
            active: walkInsDrawer.opened,
            onToggle: toggleWalkIns,
          }}
        />

        {/* Main Content */}
        <div className={styles.mainContent}>
          {/* Calendar View Area */}
          <Box
            ref={viewAreaRef}
            className={`${styles.viewArea} ${drawerOpen ? styles.viewAreaDrawerPush : ""}`}
            onPointerDownCapture={scheduling.capturePointer}
          >
            {renderView()}
          </Box>
        </div>

        {/* Floating Action Button: hidden under modals; beside an open
            drawer it shifts left instead of hiding. */}
        {!(
          scheduling.isAppointmentModalOpen ||
          store.isTransactionModalOpen ||
          scheduling.isQuickSaleModalOpen
        ) && (
          <FloatingActionButton
            shifted={drawerOpen}
            onNewAppointment={() => requestCloseThen(handleNewAppointment)}
            onNewWalkIn={() => requestCloseThen(walkInsDrawer.openNewForm)}
            onNewTimeBlock={() =>
              requestCloseThen(scheduling.handleNewTimeBlock)
            }
            onNewQuickSale={() =>
              requestCloseThen(scheduling.handleNewQuickSale)
            }
          />
        )}

        <PreviewSurfaces
          preview={preview}
          salonUuid={businessInfo?.id}
          timezone={salonTimezone}
          onRebook={booking.startRebook}
          onReschedule={booking.startReschedule}
          drawerSlot={drawerSlot}
        />

        {scheduling.slotChooserAt && (
          <SlotChooser
            position={scheduling.slotChooserAt}
            slotStart={scheduling.selectedSlot?.start ?? null}
            onAppointment={handleChooseAppointment}
            onTimeBlock={scheduling.openTimeBlockForSlot}
            onDismiss={scheduling.dismissSlotChooser}
          />
        )}

        <BookingPanel booking={booking} calendarEvents={events} />

        <WaitlistDrawer
          drawer={walkInsDrawer}
          store={waitlist}
          onAssigned={handleWalkInAssigned}
          timezone={salonTimezone}
          services={salonServices}
        />

        {sharedModals}

        <DragConfirmModal
          pendingDragConfirm={drag.pendingDragConfirm}
          isConfirmingDrag={drag.isConfirmingDrag}
          onConfirm={drag.handleConfirmDrag}
          onCancel={drag.handleCancelDrag}
        />
      </div>
    </>
  );
};

export default CalendarView;
